import { useState } from "react";
import { LifeBuoy, Plus } from "lucide-react";
import { MasterShell } from "@/shared/layouts/MasterShell.jsx";
import { Card } from "@/shared/ui/card.jsx";
import { Button } from "@/shared/ui/button.jsx";
import { Input } from "@/shared/ui/input.jsx";
import { Label } from "@/shared/ui/label.jsx";
import { Badge } from "@/shared/ui/badge.jsx";
import { Spinner, EmptyState } from "@/shared/ui/spinner.jsx";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/shared/ui/table.jsx";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/shared/ui/dialog.jsx";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/shared/ui/select.jsx";
import { formatDateTime } from "@/shared/lib/format.js";
import { useAuth } from "@/features/auth/auth-context.jsx";
import { canWriteMaster } from "@/shared/lib/roles.js";
import {
  StatCard, TicketStatusBadge, TICKET_STATUS_OPTIONS, PriorityLabel,
} from "./components/MasterUi.jsx";
import {
  useCreateTicket, useMasterClients, useMasterStats, useMasterTickets, useUpdateTicket,
} from "./use-master.js";

const TIPOS = ["Bug", "Solicitação", "Melhoria", "Chamado"];
const PRIORIDADES = ["alta", "média", "baixa"];

/** Chamados de suporte dos clientes. */
export function SupportPage() {
  const { user } = useAuth();
  const podeEscrever = canWriteMaster(user);

  const [status, setStatus] = useState("todos");
  const { data: chamados = [], isLoading } = useMasterTickets({ status });
  const { data: stats } = useMasterStats();

  const atualizar = useUpdateTicket();
  const [novoAberto, setNovoAberto] = useState(false);

  return (
    <MasterShell
      title="Suporte"
      subtitle="Chamados abertos pelos clientes"
      actions={
        podeEscrever && (
          <Button size="sm" className="gap-1.5" onClick={() => setNovoAberto(true)}>
            <Plus className="h-4 w-4" />
            Novo chamado
          </Button>
        )
      }
    >
      <div className="space-y-4 p-6">
        {stats && (
          <div className="grid gap-4 sm:grid-cols-3">
            <StatCard label="Abertos" value={stats.support.abertos} accent="warning" />
            <StatCard label="Em andamento" value={stats.support.emAndamento} accent="primary" />
            <StatCard label="Resolvidos" value={stats.support.resolvidos} accent="success" />
          </div>
        )}

        <Card className="p-4">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Situação" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todas as situações</SelectItem>
              {TICKET_STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Card>

        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-t-0 hover:bg-transparent">
                <TableHead>Chamado</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Prioridade</TableHead>
                <TableHead>Aberto em</TableHead>
                <TableHead className="w-44">Situação</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-8 text-center">
                    <Spinner className="mx-auto h-6 w-6 text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : chamados.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <EmptyState icon={LifeBuoy} title="Nenhum chamado encontrado" />
                  </TableCell>
                </TableRow>
              ) : (
                chamados.map((chamado) => (
                  <TableRow key={chamado.id}>
                    <TableCell>
                      <p className="font-medium leading-tight">{chamado.subject}</p>
                      <p className="font-mono text-[11px] text-muted-foreground">{chamado.id}</p>
                    </TableCell>
                    <TableCell className="text-sm">{chamado.clinicName}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px]">
                        {chamado.type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <PriorityLabel priority={chamado.priority} />
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatDateTime(chamado.createdAt)}
                    </TableCell>
                    <TableCell>
                      {podeEscrever ? (
                        <Select
                          value={chamado.status}
                          onValueChange={(valor) =>
                            atualizar.mutate({ id: chamado.id, status: valor })
                          }
                        >
                          <SelectTrigger className="h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {TICKET_STATUS_OPTIONS.map((o) => (
                              <SelectItem key={o.value} value={o.value}>
                                {o.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <TicketStatusBadge status={chamado.status} />
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      </div>

      <NewTicketDialog aberto={novoAberto} onClose={() => setNovoAberto(false)} />
    </MasterShell>
  );
}

function NewTicketDialog({ aberto, onClose }) {
  const criar = useCreateTicket();
  const { data: clientes = [] } = useMasterClients();

  const [form, setForm] = useState({
    clinicId: "",
    subject: "",
    type: "Chamado",
    priority: "média",
  });

  const set = (campo) => (valor) => setForm((atual) => ({ ...atual, [campo]: valor }));

  function submeter(evento) {
    evento.preventDefault();
    criar.mutate(form, {
      onSuccess: () => {
        setForm({ clinicId: "", subject: "", type: "Chamado", priority: "média" });
        onClose();
      },
    });
  }

  return (
    <Dialog open={aberto} onOpenChange={(estado) => !estado && onClose()}>
      <DialogContent>
        <form onSubmit={submeter}>
          <DialogHeader>
            <DialogTitle>Novo chamado</DialogTitle>
            <DialogDescription>Registre uma solicitação de um cliente.</DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label>Cliente</Label>
              <Select value={form.clinicId} onValueChange={set("clinicId")}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o cliente" />
                </SelectTrigger>
                <SelectContent>
                  {clientes.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.tradeName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="ticket-subject">Assunto</Label>
              <Input
                id="ticket-subject"
                required
                maxLength={200}
                value={form.subject}
                onChange={(e) => set("subject")(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select value={form.type} onValueChange={set("type")}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Prioridade</Label>
              <Select value={form.priority} onValueChange={set("priority")}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORIDADES.map((p) => (
                    <SelectItem key={p} value={p} className="capitalize">
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={criar.isPending || !form.clinicId}>
              {criar.isPending ? <Spinner /> : null}
              Abrir chamado
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
